
import React, { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Box, Button, Typography, IconButton, CircularProgress,
    FormControlLabel, Checkbox,
    Divider,
    TextField,
} from '@mui/material';
import Context from '../../context/Context';
import axios from 'axios';
import apiUrl from '../../Config';
import { toast } from 'react-toastify';

const RolePermissions = () => {
    const { permissions, getPermissions } = useContext(Context);
    const location = useLocation();
    const navigate = useNavigate();
    const role = location?.state?.role || '';

    const [selectedPermissions, setSelectedPermissions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);


    const getRolePermission = async () => {

        try {
            const response = await axios.get(apiUrl + `/rolepermission/getrolepermissions/${role.id}`)
            if (response?.data?.status === true) {
                const permissionIds = response.data.data.map(item => item.permissionId);
                setSelectedPermissions(permissionIds);
            }

        } catch (error) {
            setError(error?.response?.data?.message || 'Error for geting roles')
        }

    }

    const handleCheckboxChange = async (id) => {
        if (selectedPermissions.includes(id)) {
            // Permission is being unchecked
            try {
                const response = await removePermissionFromRole(id);
                setSelectedPermissions((prev) =>
                    prev.filter((permId) => permId !== id)
                );
                if (response?.data?.status === true) {
                    toast.success(response?.data?.message || 'Permission removed');
                }

            } catch (error) {
                setError(error?.response?.data?.message || 'Error removing permission');
            }
        } else {
            // Permission is being checked
            setSelectedPermissions((prev) => [...prev, id]);
        }
    };

    const removePermissionFromRole = async (permissionId) => {
        try {
            const response = await axios.delete(`${apiUrl}/rolepermission/deleterolepermission`, {
                headers: {
                    'Content-Type': 'application/json'
                },
                data: {
                    roleId: role.id,
                    permissionId: permissionId
                }
            });
            return response;
        } catch (error) {
            console.error("Failed to remove permission:", error);
            throw error;
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        if (!role?.id) {
            toast.error('Role ID is missing.');
            return;
        }

        if (selectedPermissions.length === 0) {
            setError('Please select at least one permission.');
            return;
        }

        const payload = {
            roleId: role.id,
            permissionId: selectedPermissions,
        };

        try {
            setLoading(true);
            const response = await axios.post(`${apiUrl}/rolepermission/rolepermission`, payload);

            if (response?.data?.status === true) {
                toast.success(response?.data?.message || 'Permissions updated successfully');
                navigate(-1); // go back
            } 
        } catch (err) {
            setError(err?.response?.data?.message || 'Error assigning role permissions');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getPermissions();
        getRolePermission()
    }, []);

    return (
        <Box sx={{ backgroundColor: "", padding: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h3">Role Permissions</Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />

            <TextField
                label="Role Name"
                size="small"
                value={role.name || ''}
                fullWidth
                InputProps={{ readOnly: true }}
                sx={{ mb: 3 }}
            />

            <Typography variant="h6" sx={{ mb: 2 }}>Select Permissions</Typography>

            {permissions.length === 0 ? (
                <CircularProgress />
            ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    {permissions.map((perm) => (
                        <FormControlLabel
                            key={perm.id}
                            control={
                                <Checkbox
                                    checked={selectedPermissions.includes(perm.id)}
                                    onChange={() => handleCheckboxChange(perm.id)}
                                />
                            }
                            label={perm.name}
                        />
                    ))}
                </Box>
            )}

            {error && (
                <Typography color="error" sx={{ mt: 2 }}>
                    {error}
                </Typography>
            )}

            <Box display="flex" justifyContent="end" mt={3} gap={2}>
                <Button variant="outlined" color="primary" onClick={() => navigate(-1)}>
                    Cancel
                </Button>
                <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={loading}
                    onClick={handleSubmit}
                >
                    {loading ? (
                        <CircularProgress size={24} sx={{ color: '#fff' }} />
                    ) : (
                        role ? 'Update' : 'Create'
                    )}
                </Button>
            </Box>
        </Box>
    );
};

export default RolePermissions;
